/**
 * `/funding-check` — the federal scholarship (IRC §25F) funding quiz.
 *
 * Shared data for the portal page AND the API route, so neither side can
 * drift: which states have filed with the IRS, the answer keys the quiz
 * stores, and the SMS-consent wording every captured number agreed to.
 *
 * ── Keeping the state list current (non-engineers: this is the only part
 *    you touch) ──────────────────────────────────────────────────────────
 *
 * Source: IRS, "Federal Scholarship Tax Credit — Participating states for
 * 2027". A state is `filed: true` once it has submitted a Form 15714 advance
 * election. The list moved in July 2026 and will move again (Treasury
 * regulations were expected by end of September 2026).
 *
 * To update: flip `filed` on the row(s) that changed, then set
 * `FUNDING_STATES_META.irsListUpdated` to the "last updated" date shown on the
 * IRS page and `lastChecked` to today. Both render on the result page; the
 * "Last checked" line is the proof the page is maintained, so bump it every
 * time the list is verified even when nothing changed.
 *
 * `hasStateProgram` is a SEPARATE fact (the state runs its own ESA/voucher
 * program). It is stored on every lead and displayed nowhere in v1 — it is
 * kept so a future third content branch is six strings, not a schema change.
 * Arizona has a program but has not filed; the two flags are independent.
 */

export interface FundingState {
  /** 2-letter USPS code — the stable key stored on the lead and sent to PostHog. */
  abbr: string;
  name: string;
  /** Has filed a Form 15714 advance election with the IRS. */
  filed: boolean;
  /** Runs its own state ESA / voucher program (not displayed in v1). */
  hasStateProgram: boolean;
}

export const FUNDING_STATES_META = {
  /** "Last updated" date on the IRS participating-states page. */
  irsListUpdated: "2026-07-24",
  /** The day a human last verified this list against the IRS page. */
  lastChecked: "2026-09-02",
  source: "IRS list of participating states",
} as const;

export const FUNDING_STATES: readonly FundingState[] = [
  { abbr: "AL", name: "Alabama", filed: true, hasStateProgram: false },
  { abbr: "AK", name: "Alaska", filed: true, hasStateProgram: false },
  { abbr: "AZ", name: "Arizona", filed: false, hasStateProgram: true },
  { abbr: "AR", name: "Arkansas", filed: true, hasStateProgram: false },
  { abbr: "CA", name: "California", filed: false, hasStateProgram: false },
  { abbr: "CO", name: "Colorado", filed: true, hasStateProgram: false },
  { abbr: "CT", name: "Connecticut", filed: false, hasStateProgram: false },
  { abbr: "DE", name: "Delaware", filed: false, hasStateProgram: false },
  { abbr: "DC", name: "District of Columbia", filed: false, hasStateProgram: false },
  { abbr: "FL", name: "Florida", filed: true, hasStateProgram: true },
  { abbr: "GA", name: "Georgia", filed: true, hasStateProgram: false },
  { abbr: "HI", name: "Hawaii", filed: false, hasStateProgram: false },
  { abbr: "ID", name: "Idaho", filed: true, hasStateProgram: false },
  { abbr: "IL", name: "Illinois", filed: false, hasStateProgram: false },
  { abbr: "IN", name: "Indiana", filed: true, hasStateProgram: false },
  { abbr: "IA", name: "Iowa", filed: true, hasStateProgram: true },
  { abbr: "KS", name: "Kansas", filed: true, hasStateProgram: false },
  { abbr: "KY", name: "Kentucky", filed: true, hasStateProgram: false },
  { abbr: "LA", name: "Louisiana", filed: true, hasStateProgram: false },
  { abbr: "ME", name: "Maine", filed: false, hasStateProgram: false },
  { abbr: "MD", name: "Maryland", filed: false, hasStateProgram: false },
  { abbr: "MA", name: "Massachusetts", filed: false, hasStateProgram: false },
  { abbr: "MI", name: "Michigan", filed: false, hasStateProgram: false },
  { abbr: "MN", name: "Minnesota", filed: false, hasStateProgram: false },
  { abbr: "MS", name: "Mississippi", filed: true, hasStateProgram: false },
  { abbr: "MO", name: "Missouri", filed: true, hasStateProgram: false },
  { abbr: "MT", name: "Montana", filed: true, hasStateProgram: false },
  { abbr: "NE", name: "Nebraska", filed: true, hasStateProgram: false },
  { abbr: "NV", name: "Nevada", filed: true, hasStateProgram: false },
  { abbr: "NH", name: "New Hampshire", filed: true, hasStateProgram: false },
  { abbr: "NJ", name: "New Jersey", filed: false, hasStateProgram: false },
  { abbr: "NM", name: "New Mexico", filed: false, hasStateProgram: false },
  { abbr: "NY", name: "New York", filed: false, hasStateProgram: false },
  { abbr: "NC", name: "North Carolina", filed: true, hasStateProgram: false },
  { abbr: "ND", name: "North Dakota", filed: true, hasStateProgram: false },
  { abbr: "OH", name: "Ohio", filed: true, hasStateProgram: false },
  { abbr: "OK", name: "Oklahoma", filed: true, hasStateProgram: false },
  { abbr: "OR", name: "Oregon", filed: false, hasStateProgram: false },
  { abbr: "PA", name: "Pennsylvania", filed: false, hasStateProgram: false },
  { abbr: "RI", name: "Rhode Island", filed: false, hasStateProgram: false },
  { abbr: "SC", name: "South Carolina", filed: true, hasStateProgram: false },
  { abbr: "SD", name: "South Dakota", filed: true, hasStateProgram: false },
  { abbr: "TN", name: "Tennessee", filed: true, hasStateProgram: false },
  { abbr: "TX", name: "Texas", filed: true, hasStateProgram: true },
  { abbr: "UT", name: "Utah", filed: true, hasStateProgram: true },
  { abbr: "VT", name: "Vermont", filed: false, hasStateProgram: false },
  { abbr: "VA", name: "Virginia", filed: true, hasStateProgram: false },
  { abbr: "WA", name: "Washington", filed: false, hasStateProgram: false },
  { abbr: "WV", name: "West Virginia", filed: true, hasStateProgram: true },
  { abbr: "WI", name: "Wisconsin", filed: false, hasStateProgram: false },
  { abbr: "WY", name: "Wyoming", filed: true, hasStateProgram: false },
];

/** Every abbreviation, in list order — the API's `state` enum. */
export const FUNDING_STATE_ABBRS = FUNDING_STATES.map((s) => s.abbr) as [string, ...string[]];

const BY_ABBR: ReadonlyMap<string, FundingState> = new Map(
  FUNDING_STATES.map((s) => [s.abbr, s] as const),
);

/** Look a state up by its 2-letter code (case-insensitive); undefined when unknown. */
export function fundingStateByAbbr(abbr: string): FundingState | undefined {
  return BY_ABBR.get(abbr.toUpperCase());
}

/** Number of states that have filed — the copy says "Thirty states have". */
export function fundingFiledCount(): number {
  return FUNDING_STATES.filter((s) => s.filed).length;
}

// ── Answer keys ─────────────────────────────────────────────────────────────
// The quiz stores KEYS, never labels: labels are copy and will be reworded;
// these keys are what PostHog funnels and the lead rows are built on. Keep them
// stable. Question order on the page is not encoded here on purpose.

/** Q1 — "Have you heard about the federal scholarship starting in 2027?" */
export const FUNDING_AWARENESS_OPTIONS = ["following", "heard_some", "new"] as const;
export type FundingAwareness = (typeof FUNDING_AWARENESS_OPTIONS)[number];

/** Q2 — "What grade is your child in?" Decides everything we ever send them. */
export const FUNDING_GRADE_BAND_OPTIONS = ["k_2", "3_5", "6_8", "9_12", "multiple"] as const;
export type FundingGradeBand = (typeof FUNDING_GRADE_BAND_OPTIONS)[number];

/** Q3 — "Where do they go to school now?" */
export const FUNDING_SCHOOL_TYPE_OPTIONS = [
  "public",
  "private",
  "homeschool",
  "charter",
  "deciding",
] as const;
export type FundingSchoolType = (typeof FUNDING_SCHOOL_TYPE_OPTIONS)[number];

/** Q4 — "If applications opened next month, how would you find out?" Branches one heading. */
export const FUNDING_HOW_FIND_OPTIONS = ["school", "news", "irs_site", "would_not"] as const;
export type FundingHowFind = (typeof FUNDING_HOW_FIND_OPTIONS)[number];

// ── Consent ─────────────────────────────────────────────────────────────────

/**
 * The SMS marketing consent a funding-check lead agrees to. The checkbox
 * LABEL is the desire statement; the SUMMARY is the TCPA disclosure rendered
 * directly beneath it (apps/portal/src/components/MarketingConsent.tsx
 * `ConsentDisclosure`, whose text a portal test pins to this string). The API
 * stores `text` verbatim on every lead, keyed by `version`, so a wording
 * change later never makes an earlier record ambiguous.
 *
 * Bump `version` whenever `label` or `summary` changes materially.
 */
export const FUNDING_CHECK_CONSENT = {
  version: "2026-09-funding-check-v1",
  label:
    "Text me when education funding opens in my state, plus occasional news from Sports Academy Games",
  summary:
    "I expressly consent to receive marketing texts, calls & voicemails from Sports Academy Games " +
    "at the number provided, including via autodialer or prerecorded voice. Consent is not a " +
    "condition of purchase. Msg & data rates may apply. Reply STOP to opt out. View our Terms of " +
    "Service and Privacy Policy.",
  get text(): string {
    return `${this.label}. ${this.summary}`;
  },
} as const;

/**
 * The OPTIONAL email consent offered on the done screen — a different channel
 * from the SMS consent above, so its own record. `label` names the state
 * because the offer is "a copy of the {State} brief"; the API renders `text`
 * from the ROW's state name, never from a client string. Nothing is emailed
 * yet: collecting the address with consent is what makes a later email
 * lawful, and Texas may require email for anything beyond the four promised
 * event texts (Tex. Bus. & Com. Code ch. 302 review pending).
 *
 * Bump `version` whenever `label` or `summary` changes materially.
 */
export const FUNDING_CHECK_EMAIL_CONSENT = {
  version: "2026-09-funding-check-email-v1",
  label: (stateName: string): string => `Email me a copy of the ${stateName} brief`,
  summary:
    "Sports Academy Games may email you this brief and updates when your state moves on the " +
    "2027 federal scholarship. Unsubscribe any time.",
  text(stateName: string): string {
    return `${this.label(stateName)}. ${this.summary}`;
  },
} as const;

/**
 * Which landing page captured a lead: the quiz (`funding_check`) or the
 * article destination `/funding-brief` (`funding_brief`), the A/B against it.
 * Stored on every `funding_leads` row so the two can be compared.
 */
export const FUNDING_LEAD_SURFACES = ["funding_check", "funding_brief"] as const;
export type FundingLeadSurface = (typeof FUNDING_LEAD_SURFACES)[number];
